import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { SatelliteMarkers } from '../satellite_tracker/SatelliteViewer';
import { TrackballControls } from '@react-three/drei';
import SatellitePreloader from '../preloaders/SatellitePreloader';
import PlanetPreloader, { planetAssets, missionAssets } from '../preloaders/PlanetPreloader';
import { normalizeAndSplitAssets } from '../../utils/normalizeAndSplitAssets';
import { loadInitialSatellites, queryAllSatellites } from '../../utils/SatelliteRTree';

import { useSarSitesData, useSarDetectionsData  } from '../../utils/SarData';
import { classifyDetections } from '../../utils/DetectionFlags';
import { DetectionMarkers } from './DetectionMarkers';
import { DetectionLegend } from './DetectionLegend';
import { colorForSite } from '../../utils/SarSiteColors';


import { formatDistanceToNow} from 'date-fns';
import './SARObservatory.css'

// Minimal, SAR-specific Earth mesh — reuses the same texture-application
// pattern as SpinningPlanet, but without the ring/rotation-speed/spin-in
// machinery that's irrelevant here (no rings on Earth, no "zoom-in from
// solar system" animation on a dedicated page).
function SarEarth({ texture }) {
  const meshRef = useRef();

  useEffect(() => {
    if (!texture || !meshRef.current) return;
    texture.flipY = true;
    texture.mapping = THREE.UVMapping;
    meshRef.current.material.map = texture;
    meshRef.current.material.needsUpdate = true;
  }, [texture]);

  if (!texture) return null;

  return (
    <mesh ref={meshRef}>
      <sphereGeometry args={[1, 64, 64]} />
      <meshStandardMaterial metalness={0.1} roughness={0.7} />
    </mesh>
  );
}

function SiteStatusBadge({ site }) {
  const lastRun = site.last_run_completed ? new Date(site.last_run_completed) : null;

  const ratio = site.overdue_ratio;

  const statusColor =
    ratio === null ? "gray" :
    ratio < 0.7 ? "green" :
    ratio < 1.0 ? "yellow" :
    "red"; // overdue relative to its own tier target

  return (
    <>
      <div className={`site-badge site-badge--${statusColor}`}>
        <div className="site-name">{site.name}</div>
        {/* <div className="site-tier">Tier {site.tier}</div> */}

        <div className="scene-count">{site.scenes_processed ?? 0} scenes last run</div>
        {lastRun ? (
          <>
            <div className="mins-ago">Updated {formatDistanceToNow(lastRun)} ago</div>
            {/* <div className="utc-time">(UTC: {lastRun.toUTCString()})</div> */}
          </>
        ) : (
          <div className="mins-ago">Never run</div>
        )}
      </div>
    </>
    
  );
}

export default function SarObservatoryPage() {
  const navigate = useNavigate();

  const sarRef=useRef(null);
  const [earthTexture, setEarthTexture] = useState(null);
  const [assetsReady, setAssetsReady] = useState(false);

  const [issModel, setISSModel] = useState(null); // unused visually but SatelliteMarkers expects the prop shape
  const [genericModel, setGenericModel] = useState(null);
  const [modelsReady, setModelsReady] = useState(false);

  const [sentinel1Satellites, setSentinel1Satellites] = useState([]);
  const [satelliteName, setSatelliteName] = useState("");
  const [satellitesReady, setSatellitesReady] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState(null);

  const { sites, fetchedAt, isLoading: sitesLoading } = useSarSitesData();

  const sortedSites = [...sites].sort(
    (a, b) => (b.overdue_ratio ?? -1) - (a.overdue_ratio ?? -1)
  );

  const { detections: rawDetections } = useSarDetectionsData();
  const [showUnconfirmed, setShowUnconfirmed] = useState(false);
  const [selectedDetection, setSelectedDetection] = useState(null);
  const [selectedSiteId, setSelectedSiteId] = useState(null);


  const detections = useMemo(() => classifyDetections(rawDetections), [rawDetections]);
  const unconfirmedCount = useMemo(
    () => detections.filter(d => d.unconfirmed).length,
    [detections]
  );

  const selectedSiteDetectionCount = detections.filter(
      (d) => d.site_id === selectedSiteId
  ).length;

  const siteNameMap = useMemo(
    () => Object.fromEntries(sites.map(s => [s.id, s.name])),
    [sites]
  );

  const selectedSite = useMemo(
    () => sites.find(s => s.id === selectedSiteId) || null,
    [sites, selectedSiteId]
  );

  // Load just Earth's texture + model — reuses the existing preloader
  // rather than duplicating its asset list.

  // Sentinel-1 only — loaded once, independent of the generic satellite
  // filter dashboard (this page doesn't need category toggles at all).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await loadInitialSatellites();
      if (cancelled) return;
      const results = queryAllSatellites({ name: 'sentinel-1', matchMode: 'startsWith' });
      setSentinel1Satellites(results);
      setSatelliteName(results[0]?.name);
      setSatellitesReady(true);
    })();
    return () => { cancelled = true; };
  }, []);

  const handleClusterClick = useCallback((cluster) => {
    setSelectedCluster(cluster);
    setSelectedSiteId(cluster.id);
  }, []);
  
  // const detectionsBySite = useMemo(() => {
  //   const map = new Map();

  //   for (const d of detections) {
  //     const id = d.site_id;
  //     map.set(id, (map.get(id) || 0) + 1);
  //   }

  //   return map;
  // }, [detections]);

  // console.table(
  //   sites.map(site => ({
  //     site_id: site.id,
  //     site: site.name,
  //     detections: detectionsBySite.get(site.id) || 0,
  //   }))
  // );
  // const rotterdam = detections.filter(
  //   d => d.site_id === "rotterdam"
  // );

  // console.log("Rotterdam detections:", rotterdam.length);
  // console.log("First Rotterdam:", rotterdam[0]);

  // console.table(
  //   rotterdam.slice(0, 20).map(d => ({
  //     id: d.id,
  //     site_id: d.site_id,
  //     latitude: d.latitude,
  //     longitude: d.longitude,
  //     lat: d.lat,
  //     lon: d.lon,
  //     coordinates: d.coordinates,
  //     geometry: d.geometry,
  //     unconfirmed: d.unconfirmed,
  //   }))
  // );

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000' }}>
      <PlanetPreloader
        skipAnimation={true}
        onComplete={(loadedAssets) => {
          const { planetTextures, planetModel } = normalizeAndSplitAssets(
            loadedAssets,
            [...planetAssets, ...missionAssets]
          );
          setEarthTexture(planetTextures.earth);
          setAssetsReady(true);
        }}
      />

      <SatellitePreloader
        onComplete={(loadedAssets) => {
          setGenericModel(loadedAssets.satellite); // Sentinel-1 renders via the generic satellite dot/model path
          setModelsReady(true);
        }}
      />

      {assetsReady && modelsReady && (
        <>
          <Canvas className="sar-canvas-container"
            camera={{ position: [3, 2, 10], fov: 45 }}
            gl={{ outputEncoding: THREE.SRGBColorSpace }}
            onCreated={({ gl }) => {
              gl.toneMapping = THREE.ACESFilmicToneMapping;
              gl.toneMappingExposure = 1;
            }}
          >
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 10]} intensity={0.25} />
            <directionalLight position={[-5, -5, -10]} intensity={0.35} />

            <SarEarth texture={earthTexture} />

            <TrackballControls
              rotateSpeed={4.0}
              zoomSpeed={1.2}
              panSpeed={0}
              noPan
              enableZoom
              enablePan={false}
              staticMoving
              minDistance={1.5}
              maxDistance={5}
            />

            {satellitesReady && (
              <SatelliteMarkers
                satellites={sentinel1Satellites}
                issModel={issModel}
                genericModel={genericModel}
                onClusterClick={handleClusterClick}
                selectedCluster={selectedCluster}
                onReady={() => {}}
                onNotReady={() => {}}
                baseScale={0.35}
                enableSelectionColor={true}
                showAllOrbits={true}
              />
            )}

            <DetectionMarkers
              detections={detections}
              showUnconfirmed={showUnconfirmed}
              selectedSiteId={selectedSiteId}
              onSelectSite={setSelectedSiteId}
            />
          </Canvas>
          <div>
            
          </div>
          <div className="sar-overlay">           
            <DetectionLegend
              showUnconfirmed={showUnconfirmed}
              onToggle={setShowUnconfirmed}
              unconfirmedCount={unconfirmedCount}
              totalCount={detections.length}
              siteNameMap={siteNameMap}
            />

            {selectedSite && (
              <div className="site-popup-bubble">
                <div><h4> {satelliteName} Detections:</h4></div>

                <div className="site-popup-site-row">
                  <span
                    className="legend-dot"
                    style={{ background: `#${colorForSite(selectedSiteId).getHexString()}` }}
                  />
                  <div className="site-popup-name">{selectedSite.name}</div>
                </div>
                <div className="site-popup-detections">
                  {selectedSiteDetectionCount}
                </div>
                <div className="site-popup-updated">
                  {selectedSite.last_run_completed
                    ? `Last Updated: ${formatDistanceToNow(new Date(selectedSite.last_run_completed))} ago`
                    : 'Never run'}
                </div>
              </div>
            )}
            {/* <div className="site-badge-row">
              {!sitesLoading &&
                sortedSites.map((site) => (
                  <SiteStatusBadge key={site.id} site={site} />
                ))}
            </div> */}
          </div>

        </>
      )}    

      <div className="back-button">
        <button onClick={() => navigate('/')}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path
              fillRule="evenodd"
              d="M15 8a.5.5 0 0 1-.5.5H3.707l4.147 4.146a.5.5 0 0 1-.708.708l-5-5a.5.5 0 0 1 0-.708l5-5a.5.5 0 1 1 .708.708L3.707 7.5H14.5a.5.5 0 0 1 .5.5z"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}